// What each alarm receiver actually gets for an alert, in plain words
// (ENG-2483). Every line here was checked against the sending code, not the
// field names. Sources (verified 2026-10-19):
//   actuate-libraries origin/main, package actuate-alarm-senders 1.21.0
//     (the version the prod connector, vms-connector rearchitecture, pins)
//   queue_consumer origin/main (develop differs only in retries/timeouts)
//   actuate_admin origin/main inframap/integrations/<name>/ (settings sent
//     to the connector)
// Applies to several receivers: "alert images" are the site's detection
// frames (count = the site's attachment frames setting, not a per-alert
// field), and the ignore-zone / video switches are combined per camera with
// any(): if one alert of that type on the camera has it on, all of them get it.

import { isPatriotSendVideoOn } from "../../../common/PatriotDrawIgnoreZonesGate";
import { ReceiverKey, isOn } from "./receiverConfigs";

export interface ReceiverDescription {
  headline: string;
  note?: string;
}

/** Explains the ignore-zones choice where the receiver gets images. */
export const DRAW_IGNORE_ZONES_COPY: Partial<
  Record<ReceiverKey, { appliesTo: string; note: string }>
> = {
  // qc consumers/immix/immix_consumer.py::send -> shared/utils.py::get_images
  // draws zones (alert_utils/bounding_box.py::draw_boxes) on the attached
  // JPEGs, and on the video frames when the site sends video.
  immix: {
    appliesTo: "the attached alert images",
    note: "Shared by every Immix alert on this camera. Zones are also drawn when the site or group has ignore zones drawing turned on.",
  },
  // qc consumers/dice/dice_consumer.py: same code path as Immix.
  dice: {
    appliesTo: "the attached alert images",
    note: "Shared by every Dice alert on this camera. Zones are also drawn when the site or group has ignore zones drawing turned on.",
  },
  // qc consumers/sentinel/sentinel_consumer.py::get_sentinel_attachments draws
  // zones on the JPEGs it uploads; admin sends the per-alert flag only.
  sentinel: {
    appliesTo: "the alert images",
    note: "Shared by every Sentinel alert on this camera.",
  },
};

const withIds = (name: string, ids: (string | false | undefined)[]) => {
  const shown = ids.filter(Boolean);
  return shown.length ? `${name} (${shown.join(", ")})` : name;
};

const value = (raw: unknown): string | undefined =>
  raw === null || raw === undefined || raw === "" ? undefined : String(raw);

const imagesLine = (drawIgnoreZones: unknown) =>
  isOn(drawIgnoreZones) ? " (ignore zones drawn)" : "";

/**
 * Plain-language line for one stored alert. `alert` is the stored row (or
 * the row with unsaved edits merged in, so the line follows the form).
 */
export const describeReceiver = (
  key: ReceiverKey,
  alert: any,
  { ignoreZonesOnAlertPage = false }: { ignoreZonesOnAlertPage?: boolean } = {}
): ReceiverDescription => {
  switch (key) {
    // libs immix/immix_alert_sender.py::send; qc consumers/immix/
    // immix_consumer.py::send. SMTP email: Immix XML (<URL> = alert page
    // link) plus JPEG attachments. Video only via the site/group feature
    // immix_use_mp4 (admin inframap/integrations/immix/immix_alert.py).
    case "immix":
    case "dice": {
      const name = key === "immix" ? "Immix" : "Dice";
      const input = value(alert[`${key}_input1`]);
      return {
        headline: `${withIds(name, [
          input && `input ${input}`,
        ])} gets an email with the alert images${imagesLine(
          alert.draw_ignore_zones
        )} and a link to the alert page`,
        note: "If video is turned on for the site or group, a video file is attached instead of the images.",
      };
    }
    // libs sureview/sureview_alert_sender.py; qc consumers/sureview/
    // sureview_consumer.py::get_attachments. SMTP email (port 587) with the
    // detection frames attached, boxes for every label; no zones, ever.
    case "sureview": {
      const email = value(alert.sureview_email);
      return {
        headline: `${withIds("SureView", [
          email && `to ${email}`,
        ])} gets an email with the alert images and a link to the alert page`,
        note: "Ignore zones are not drawn for SureView.",
      };
    }
    // libs sentinel/sentinel_alert_sender.py; qc consumers/sentinel/
    // sentinel_consumer.py::send. HTTP POST with MediaUrl= links to JPEGs we
    // host; no alert page link. Site ID wins over device address.
    case "sentinel": {
      const site = value(alert.sentinel_site_id);
      const device = value(alert.sentinel_device_address);
      return {
        headline: `${withIds("Sentinel", [
          site ? `site ${site}` : device && `device ${device}`,
        ])} gets links to the alert images${imagesLine(
          alert.draw_ignore_zones
        )}`,
      };
    }
    // libs bold/bold_alert_sender.py::BoldSocket.makeAlarm. TCP alarm packet
    // from the connector; <URL> = alert page link; image code is disabled.
    case "bold": {
      const id = value(alert.bold_id);
      const zone = value(alert.bold_zone);
      return {
        headline: `${withIds("Bold", [
          id && `ID ${id}`,
          zone && `zone ${zone}`,
        ])} gets an alarm signal with a link to the alert page`,
        note: "No images are sent.",
      };
    }
    // libs patriot/patriot_alert_sender.py::send / with_ignore_zones_param;
    // qc consumers/patriot/patriot_consumer.py. media = alert page link
    // (plus &iz=1 when drawing zones), or a link to an MP4 we host when
    // patriot_send_video is on (zones then burned into the video).
    case "patriot": {
      const client = value(alert.patriot_client_no);
      const zone = value(alert.patriot_zone_number);
      const who = withIds("Patriot", [
        client && `client ${client}`,
        zone && `zone ${zone}`,
      ]);
      const video = isPatriotSendVideoOn(alert.patriot_send_video);
      const zones = isPatriotSendVideoOn(alert.draw_ignore_zones);
      let what: string;
      if (video) {
        what = `gets a link to an MP4 video clip of the alert${
          zones ? " with ignore zones drawn" : ""
        }`;
      } else if (zones && ignoreZonesOnAlertPage) {
        what = "gets a link to the alert page, with ignore zones drawn";
      } else {
        what = "gets a link to the alert page";
      }
      return {
        headline: `${who} ${what}`,
        note: video
          ? "The alert page link is not sent. If the clip can't be made, Patriot gets the alert page link instead."
          : "The alert images and clip play on the alert page.",
      };
    }
    // libs softguard/softguard_alert_sender.py; qc consumers/softguard/
    // softguard_consumer.py::send. HTTPS event, TextData = alert page link;
    // attachments are never sent.
    case "softguard": {
      const account = value(alert.account);
      const zone = value(alert.zone);
      return {
        headline: `${withIds("SoftGuard", [
          account && `account ${account}`,
          zone && `zone ${zone}`,
        ])} gets an alarm event with a link to the alert page`,
        note: "No images are sent.",
      };
    }
    // libs evalink/alert_sender.py; qc consumers/evalink/evalink_consumer.py.
    // Talos alarm whose attachment widget embeds the alert page.
    case "evalink": {
      const device = value(alert.evalink_device_id);
      return {
        headline: `${withIds("Evalink", [
          device && `device ${device}`,
        ])} gets an alarm that shows the alert page inside Evalink`,
        note: "No images are sent. Only the first Evalink alert on a camera is used.",
      };
    }
    // libs sysaid/sysaid_alert_sender.py: send() is a no-op; send_chm() opens
    // an incident for camera health alerts only.
    case "sysaid": {
      const client = value(alert.sysaid_client_local);
      return {
        headline: `${withIds("SysAid", [
          client && `client ${client}`,
        ])} gets a ticket when this camera has a health problem`,
        note: "Detection alerts are not sent to SysAid.",
      };
    }
    default:
      return { headline: "" };
  }
};

const WEBHOOK_EVENT_NAMES: Record<string, string> = {
  detection: "Detection",
  no_motion: "No Motion",
  unable_to_connect: "Unable to Connect",
};

// libs webhook/webhook_alert_sender.py; qc consumers/webhook/
// webhook_consumer.py::send_webhook. The body is the webhook's own template
// with custom fields merged in; the alert link ({alert_url}) and images
// ({alert_image}, {alert_images}) go out only if the template asks for them.
export const describeWebhook = (webhook: any): ReceiverDescription => {
  const name = value(webhook?.webhook?.display_name);
  const events: string[] = (webhook?.event_types ?? [])
    .map((type: string) => WEBHOOK_EVENT_NAMES[type])
    .filter(Boolean);
  const who = name ? `${name} webhook` : "This webhook";
  return {
    headline: events.length
      ? `${who} gets a message for: ${events.join(", ")}`
      : `${who} has no events selected, so it gets nothing`,
    note: "The message follows the webhook's template. It includes the alert link or images only if the template asks for them.",
  };
};
